// Sign in, sign up, forgot password, reset password.
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, auth } from "../api.ts";
import { useSession } from "../session.tsx";
import { AuthCard, Field } from "../components/ui.tsx";

function useSubmit() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (e: FormEvent, fn: () => Promise<void>) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run };
}

export function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { busy, error, run } = useSubmit();
  const { refresh } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") ?? "/";
  return (
    <AuthCard>
      <h1>Welcome back</h1>
      <form
        onSubmit={(e) =>
          run(e, async () => {
            await auth.signIn(email, password);
            await refresh();
            navigate(next, { replace: true });
          })
        }
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        <Field id="email" label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" autoFocus />
        <Field id="password" label="Password" type="password" value={password} onChange={setPassword} autoComplete="current-password" />
        {error && <p className="error" role="alert">{error}</p>}
        <button className="btn btn-primary" disabled={busy}>Sign in</button>
      </form>
      <div className="row" style={{ justifyContent: "space-between", fontSize: 14 }}>
        <Link to="/forgot">Forgot password?</Link>
        <span className="muted">New here? You'll need an invite link.</span>
      </div>
    </AuthCard>
  );
}

/** The sign-up form, used on the invite page (with a code) and for the bootstrap owner (without). */
export function SignUpForm({ inviteCode, onDone }: { inviteCode?: string; onDone: (serverId: string | null) => void }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { busy, error, run } = useSubmit();
  const { refresh } = useSession();
  return (
    <form
      onSubmit={(e) =>
        run(e, async () => {
          const r = await api<{ serverId: string | null }>("/signup", { body: { name, email, password, inviteCode } });
          await refresh();
          onDone(r.serverId);
        })
      }
      style={{ display: "flex", flexDirection: "column", gap: 14 }}
    >
      <Field id="name" label="Display name" value={name} onChange={setName} autoComplete="nickname" autoFocus />
      <Field id="email" label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" />
      <Field id="password" label="Password (8+ characters)" type="password" value={password} onChange={setPassword} autoComplete="new-password" minLength={8} />
      {error && <p className="error" role="alert">{error}</p>}
      <button className="btn btn-primary" disabled={busy}>Create account</button>
    </form>
  );
}

export function SignUp() {
  const navigate = useNavigate();
  return (
    <AuthCard>
      <h1>Create the first account</h1>
      <p className="muted" style={{ margin: 0 }}>Only the owner's email can sign up without an invite.</p>
      <SignUpForm onDone={() => navigate("/", { replace: true })} />
      <p style={{ margin: 0, fontSize: 14 }}>Already have an account? <Link to="/login">Sign in</Link></p>
    </AuthCard>
  );
}

export function Forgot() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const { busy, error, run } = useSubmit();
  return (
    <AuthCard>
      <h1>Reset your password</h1>
      {sent ? (
        <p style={{ margin: 0 }}>If there's an account for <strong>{email}</strong>, we've emailed it a link. It works once, for an hour.</p>
      ) : (
        <form onSubmit={(e) => run(e, async () => { await auth.requestReset(email); setSent(true); })} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Field id="email" label="Email" type="email" value={email} onChange={setEmail} autoComplete="email" autoFocus />
          {error && <p className="error" role="alert">{error}</p>}
          <button className="btn btn-primary" disabled={busy}>Email me a link</button>
        </form>
      )}
      <p style={{ margin: 0, fontSize: 14 }}><Link to="/login">Back to sign in</Link></p>
    </AuthCard>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [done, setDone] = useState(false);
  const { busy, error, run } = useSubmit();
  if (!token || params.get("error")) {
    return (
      <AuthCard>
        <h1>That link didn't work</h1>
        <p style={{ margin: 0 }}>Reset links work once and expire after an hour.</p>
        <Link className="btn btn-primary" to="/forgot">Send a new link</Link>
      </AuthCard>
    );
  }
  return (
    <AuthCard>
      <h1>Choose a new password</h1>
      {done ? (
        <>
          <p style={{ margin: 0 }}>Done. You've been signed out everywhere else.</p>
          <Link className="btn btn-primary" to="/login">Sign in</Link>
        </>
      ) : (
        <form onSubmit={(e) => run(e, async () => { await auth.resetPassword(token, password); setDone(true); })} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Field id="password" label="New password (8+ characters)" type="password" value={password} onChange={setPassword} autoComplete="new-password" minLength={8} autoFocus />
          {error && <p className="error" role="alert">{error}</p>}
          <button className="btn btn-primary" disabled={busy}>Save password</button>
        </form>
      )}
    </AuthCard>
  );
}
